import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { ScrollToTop } from "./components/ScrollToTop";
import { TranslationProvider } from "./providers/TranslationContext";
import { AuthProvider } from "./lib/AuthProvider";
import { personas } from "./data/personas";

// Lazy load all components to reduce initial bundle size
const IndexOriginal = lazy(() => import("./pages/IndexOriginal"));
const Index2 = lazy(() => import("./pages/Index2"));
const Index3 = lazy(() => import("./pages/Index3"));
const Landing2 = lazy(() => import("./pages/Landing2"));
const Landing4 = lazy(() => import("./pages/Landing4"));
const CoachWorld = lazy(() => import("./pages/CoachWorld"));
const CWWebsite = lazy(() => import("./pages/CWWebsite"));
const CWBooking = lazy(() => import("./pages/CWBooking"));
const CWMarketing = lazy(() => import("./pages/CWMarketing"));
const CWAbout = lazy(() => import("./pages/CWAbout"));
const AboutLanding4 = lazy(() => import("./pages/AboutLanding4"));
const ProLanding = lazy(() => import("./pages/ProLanding"));
const Landing5 = lazy(() => import("./pages/Landing5"));
const WhiteLabel = lazy(() => import("./pages/WhiteLabel"));
const WebsiteBuilder = lazy(() => import("./pages/WebsiteBuilder"));
const BookingSystem = lazy(() => import("./pages/BookingSystem"));
const MarketingAutomation = lazy(() => import("./pages/MarketingAutomation").then(m => ({ default: m.MarketingAutomation })));
const BrandStrategy = lazy(() => import("./pages/BrandStrategy"));
const AdminFinance = lazy(() => import("./pages/AdminFinance"));
const CustomerRelations = lazy(() => import("./pages/CustomerRelations"));
const DailyAssistant = lazy(() => import("./pages/DailyAssistant"));
const PersonaPage = lazy(() => import("./pages/persona/PersonaPage").then(m => ({ default: m.PersonaPage })));
const Partners = lazy(() => import("./pages/Partners"));
const PartnersReg = lazy(() => import("./pages/PartnersReg").then(m => ({ default: m.PartnersReg })));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const NewAdmin = lazy(() => import("./pages/NewAdmin"));
const NewAdmin2 = lazy(() => import("./pages/NewAdmin2"));
const UserAdmin2 = lazy(() => import("./pages/UserAdmin2"));
const UserAdmin3 = lazy(() => import("./pages/UserAdmin3"));
const AboutUs2 = lazy(() => import("./pages/AboutUs2"));
const PricingPage = lazy(() => import("./pages/Pricing"));
const Pricing2 = lazy(() => import("./pages/Pricing2"));
const Pricing3 = lazy(() => import("./pages/Pricing3"));
const SMBPricing = lazy(() => import("./pages/SMBPricing"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const Investor = lazy(() => import("./pages/Investor"));
const TSMTPitch = lazy(() => import("./pages/TSMTPitch"));
const TSMTPitchTherapists = lazy(() => import("./pages/TSMTPitchTherapists"));
const TSMTWebinar = lazy(() => import("./pages/TSMTWebinar"));
const TSMTPartner = lazy(() => import("./pages/TSMTPartner"));
const TSMTKommunikacioPlaybook = lazy(() => import("./pages/TSMTKommunikacioPlaybook"));
const Contractors = lazy(() => import("./pages/Contractors"));
const ForUsers = lazy(() => import("./pages/ForUsers"));
const Users = lazy(() => import("./pages/Users"));
const UserLogin = lazy(() => import("./pages/users/Login"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Onboarding2 = lazy(() => import("./pages/Onboarding2"));
const Onboarding3 = lazy(() => import("./pages/Onboarding3"));
const Onboarding4 = lazy(() => import("./pages/Onboarding4"));
const Onboarding5 = lazy(() => import("./pages/Onboarding5"));
const AsszisztensNaptar = lazy(() => import("./pages/AsszisztensNaptar"));
const Selection = lazy(() => import("./pages/Selection"));
const AiAssistant = lazy(() => import("./pages/AiAssistant"));
const AiAssistant2 = lazy(() => import("./pages/AiAssistant2"));
const AiAssistant3 = lazy(() => import("./pages/AiAssistant3"));
const InstagramAnalyzer = lazy(() => import("./pages/InstagramAnalyzer"));

// VRichard pages
const VRichard = lazy(() => import("./pages/VRichard"));
const VRichardProductDetail = lazy(() => import("./pages/VRichardProductDetail"));
const VRichardServiceDetail = lazy(() => import("./pages/VRichardServiceDetail"));
const VRichardBooking = lazy(() => import("./pages/VRichardBooking"));

// Photographer pages
const VojcehovszkijRichard = lazy(() => import("./pages/VojcehovszkijRichard"));
const NyulAndreaContent = lazy(() => import("./pages/NyulAndreaContent"));
const NyulAndreaContentDetails = lazy(() => import("./pages/NyulAndreaContentDetails"));

// Face Team pages
const FaceTeam = lazy(() => import("./pages/FaceTeam"));
const FaceTeamAboutUs = lazy(() => import("./pages/FaceTeamAboutUs"));
const FaceTeamShows = lazy(() => import("./pages/FaceTeamShows"));
const FaceTeamShowDetail = lazy(() => import("./pages/FaceTeamShowDetail"));
const FaceTeamContent = lazy(() => import("./pages/FaceTeamContent"));
const FaceTeamContentDetails = lazy(() => import("./pages/FaceTeamContentDetails"));
const FaceTeamServiceCategory = lazy(() => import("./pages/FaceTeamServiceCategory"));
const FaceTeamServiceDetail = lazy(() => import("./pages/FaceTeamServiceDetail"));
const FaceTeamChat = lazy(() => import("./pages/FaceTeamChat"));
const FaceTeamVideoMessage = lazy(() => import("./pages/FaceTeamVideoMessage"));

// Gonda Cili pages
const GondaCili = lazy(() => import("./pages/GondaCili"));
const GondaCiliKapcsolat = lazy(() => import("./pages/GondaCiliKapcsolat"));
const WorkshopDetail = lazy(() => import("./pages/WorkshopDetail"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const IdopontFoglalas = lazy(() => import("./pages/IdopontFoglalas"));

// User Admin pages
const UserAdmin = lazy(() => import("./pages/UserAdmin"));
const UserDashboard = lazy(() => import("./pages/user-admin/UserDashboard"));
const UserProfile = lazy(() => import("./pages/user-admin/UserProfile"));
const UserPurchases = lazy(() => import("./pages/user-admin/UserPurchases"));
const UserAppointments = lazy(() => import("./pages/user-admin/UserAppointments"));
const UserWorkshops = lazy(() => import("./pages/user-admin/UserWorkshops"));
const UserVideoRequests = lazy(() => import("./pages/user-admin/UserVideoRequests"));
const UserQuestions = lazy(() => import("./pages/user-admin/UserQuestions"));

// AnyaHasa pages
const AnyaHasa = lazy(() => import("./pages/AnyaHasa"));
const AnyaHasaPersonalVideo = lazy(() => import("./pages/AnyaHasaPersonalVideo"));
const AnyaHasaChat = lazy(() => import("./pages/AnyaHasaChat"));
const AnyaHasaOneToOneConsultation = lazy(() => import("./pages/AnyaHasaOneToOneConsultation"));
const AnyaHasaGroupConsultation = lazy(() => import("./pages/AnyaHasaGroupConsultation"));
const AnyaHasaPostpartumProgram = lazy(() => import("./pages/AnyaHasaPostpartumProgram"));
const AnyaHasaGroupPilatesProgram = lazy(() => import("./pages/AnyaHasaGroupPilatesProgram"));
const AnyaHasaDigitalContent = lazy(() => import("./pages/AnyaHasaDigitalContent"));
const AnyaHasaInteractive = lazy(() => import("./pages/AnyaHasaInteractive"));

// Tanulom Magam pages
const TanulomMagamNew = lazy(() => import("./pages/TanulomMagamNew"));
const TanulomMagamRolamNew = lazy(() => import("./pages/TanulomMagamRolamNew"));
const TanulomMagamKapcsolatNew = lazy(() => import("./pages/TanulomMagamKapcsolatNew"));
const TanulomMagamPszichodieta = lazy(() => import("./pages/TanulomMagamPszichodieta"));
const TanulomMagamIdomenedzsment = lazy(() => import("./pages/TanulomMagamIdomenedzsment"));

// Sarkozi Akos pages
const SarkoziAkos = lazy(() => import("./pages/SarkoziAkos"));
const PersonalVideo = lazy(() => import("./pages/PersonalVideo"));
const PrivateMessage = lazy(() => import("./pages/PrivateMessage"));
const DigitalContent = lazy(() => import("./pages/DigitalContent"));
const Consultation = lazy(() => import("./pages/Consultation"));
const GroupConsultation = lazy(() => import("./pages/GroupConsultation"));
const Packages = lazy(() => import("./pages/Packages"));

// Szakács Geri pages
const SzakacsGeri = lazy(() => import("./pages/SzakacsGeri"));
const SzakacsGeriChat = lazy(() => import("./pages/szakacsgeri/Chat"));
const SzakacsGeriVideoMessage = lazy(() => import("./pages/szakacsgeri/VideoMessage"));
const SzakacsGeriContent = lazy(() => import("./pages/szakacsgeri/Content"));
const SzakacsGeriGroupMeeting = lazy(() => import("./pages/szakacsgeri/GroupMeeting"));
const SzakacsGeriBundle = lazy(() => import("./pages/szakacsgeri/Bundle"));

// Burjan Fanni pages
const BurjanFanni = lazy(() => import("./pages/BurjanFanni"));
const BurjanFanniInteractive = lazy(() => import("./pages/BurjanFanniInteractive"));
const BurjanFanniInteractiveDetails = lazy(() => import("./pages/BurjanFanniInteractiveDetails"));
const BurjanFanniSubscription = lazy(() => import("./pages/BurjanFanniSubscription"));
const BurjanFanniPersonalVideoSelect = lazy(() => import("./pages/BurjanFanniPersonalVideoSelect"));
const BurjanFanniPersonalVideoForm = lazy(() => import("./pages/BurjanFanniPersonalVideoForm"));
const BurjanFanniPrivateMessage = lazy(() => import("./pages/BurjanFanniPrivateMessage"));
const BurjanFanniDigitalContent = lazy(() => import("./pages/BurjanFanniDigitalContent"));
const BurjanFanniDigitalContentDetails = lazy(() => import("./pages/BurjanFanniDigitalContentDetails"));
const BurjanFanniConsultation = lazy(() => import("./pages/BurjanFanniConsultation"));
const BurjanFanniGroupConsultation = lazy(() => import("./pages/BurjanFanniGroupConsultation"));
const BurjanFanniPackages = lazy(() => import("./pages/BurjanFanniPackages"));
const BurjanFanniPackageDetails = lazy(() => import("./pages/BurjanFanniPackageDetails"));
const BurjanFanniPersonalMeeting = lazy(() => import("./pages/BurjanFanniPersonalMeeting"));
const BurjanFanniPersonalMeetingForm = lazy(() => import("./pages/BurjanFanniPersonalMeetingForm"));

// PumpedGabo pages
const PumpedGabo = lazy(() => import("./pages/PumpedGabo"));
const Gaboland = lazy(() => import("./pages/pumpedgabo/Gaboland"));
const PumpedGaboInteractive = lazy(() => import("./pages/pumpedgabo/PumpedGaboInteractive"));
const GabolandVoting = lazy(() => import("./pages/pumpedgabo/GabolandVoting"));
const GabolandQuiz = lazy(() => import("./pages/pumpedgabo/GabolandQuiz"));
const GabolandChallenge = lazy(() => import("./pages/pumpedgabo/GabolandChallenge"));
const GabolandGiveaway = lazy(() => import("./pages/pumpedgabo/GabolandGiveaway"));
const PumpedGaboPrivateMessage = lazy(() => import("./pages/pumpedgabo/PrivateMessage"));
const PumpedGaboPersonalVideo = lazy(() => import("./pages/pumpedgabo/PersonalVideo"));
const PumpedGaboGroupConsultation = lazy(() => import("./pages/pumpedgabo/GroupConsultation"));
const PumpedGaboPackages = lazy(() => import("./pages/pumpedgabo/Packages"));
const PumpedGaboPackageDetails = lazy(() => import("./pages/pumpedgabo/PackageDetails"));
const PumpedGaboPersonalMeeting = lazy(() => import("./pages/pumpedgabo/PersonalMeeting"));
const PumpedGaboWorkshop = lazy(() => import("./pages/pumpedgabo/Workshop"));
const WorkshopDetails = lazy(() => import("./pages/pumpedgabo/WorkshopDetails"));
const DigitalContentDetails = lazy(() => import("./pages/pumpedgabo/DigitalContentDetails"));
const PumpedGaboDigitalContent = lazy(() => import("./pages/pumpedgabo/DigitalContent"));

// Admin pages
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const Messages = lazy(() => import("./pages/admin/Messages"));
const Customers = lazy(() => import("./pages/admin/Customers"));
const Content = lazy(() => import("./pages/admin/Content"));
const ContentEdit = lazy(() => import("./pages/admin/ContentEdit"));
const PackageEdit = lazy(() => import("./pages/admin/PackageEdit"));
const PageSetup = lazy(() => import("./pages/admin/PageSetup"));
const GroupCalls = lazy(() => import("./pages/admin/GroupCalls"));
const Workshops = lazy(() => import("./pages/admin/Workshops"));
const Subscriptions = lazy(() => import("./pages/admin/Subscriptions"));
const Finances = lazy(() => import("./pages/admin/Finances"));
const Reviews = lazy(() => import("./pages/admin/Reviews"));
const AIBuilder = lazy(() => import("./pages/admin/AIBuilder"));
const InteractiveData = lazy(() => import("./pages/admin/InteractiveData"));
const DomainSelection = lazy(() => import("./pages/admin/DomainSelection"));
const DomainSetup = lazy(() => import("./pages/admin/DomainSetup"));
const DomainPurchase = lazy(() => import("./pages/admin/DomainPurchase"));
const SubdomainSuccess = lazy(() => import("./pages/admin/SubdomainSuccess"));
const PublishRegistration = lazy(() => import("./pages/admin/PublishRegistration").then(m => ({ default: m.PublishRegistration })));
const SocialOverview = lazy(() => import("./pages/admin/social/SocialOverview"));
const SocialPostEditor = lazy(() => import("./pages/admin/social/PostEditor"));
const SocialStoryEditor = lazy(() => import("./pages/admin/social/StoryEditor"));
const SocialCalendarPage = lazy(() => import("./pages/admin/social/SocialCalendar"));

// Auth pages
const Admin2 = lazy(() => import("./pages/Admin2"));
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const Login2 = lazy(() => import("./pages/Login2"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Rounds = lazy(() => import("./pages/Rounds"));
const CreatorWorld = lazy(() => import("./pages/CreatorWorld"));
const CreatorWorld2 = lazy(() => import("./pages/CreatorWorld2"));
const CreatorWorld3 = lazy(() => import("./pages/CreatorWorld3"));

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TranslationProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<IndexOriginal />} />
            <Route path="/index2" element={<Index2 />} />
            <Route path="/landing3" element={<Index3 />} />
            <Route path="/landing2" element={<Landing2 />} />
            <Route path="/landing4" element={<Landing4 />} />
            <Route path="/coachworld" element={<CoachWorld />} />
            <Route path="/coachworld/website" element={<CWWebsite />} />
            <Route path="/coachworld/booking" element={<CWBooking />} />
            <Route path="/coachworld/marketing" element={<CWMarketing />} />
            <Route path="/coachworld/about" element={<CWAbout />} />
            <Route path="/landing4/about" element={<AboutLanding4 />} />
            <Route path="/landing4/pro" element={<ProLanding />} />
            <Route path="/landing5" element={<Landing5 />} />
            <Route path="/white-label" element={<WhiteLabel />} />
            <Route path="/website-builder" element={<WebsiteBuilder />} />
            <Route path="/booking-system" element={<BookingSystem />} />
            <Route path="/marketing-automation" element={<MarketingAutomation />} />
            <Route path="/brand-strategy" element={<BrandStrategy />} />
            <Route path="/admin-finance" element={<AdminFinance />} />
            <Route path="/customer-relations" element={<CustomerRelations />} />
            <Route path="/daily-assistant" element={<DailyAssistant />} />
            <Route path="/investor" element={<Investor />} />
            <Route path="/for/yoga-pilates-instructors" element={<PersonaPage persona={personas["yoga-pilates-instructors"]} />} />
            <Route path="/for/fitness-personal-trainers" element={<PersonaPage persona={personas["fitness-personal-trainers"]} />} />
            <Route path="/for/beauticians-beauty-salons" element={<PersonaPage persona={personas["beauticians-beauty-salons"]} />} />
            <Route path="/for/massage-therapists" element={<PersonaPage persona={personas["massage-therapists"]} />} />
            <Route path="/for/consultants-business-coaches" element={<PersonaPage persona={personas["consultants-business-coaches"]} />} />
            <Route path="/for/hairdressers-barbers" element={<PersonaPage persona={personas["hairdressers-barbers"]} />} />
            <Route path="/for/photographers-videographers" element={<PersonaPage persona={personas["photographers-videographers"]} />} />
            <Route path="/for/nail-technicians" element={<PersonaPage persona={personas["nail-technicians"]} />} />
            <Route path="/for/physiotherapists" element={<PersonaPage persona={personas["physiotherapists"]} />} />
            <Route path="/for/sport-clubs" element={<PersonaPage persona={personas["sport-clubs"]} />} />
            <Route path="/for/psychologists-mentors" element={<PersonaPage persona={personas["psychologists-mentors"]} />} />
            <Route path="/for/nutritionists" element={<PersonaPage persona={personas["nutritionists"]} />} />
            <Route path="/for/therapists-coaches" element={<PersonaPage persona={personas["therapists-coaches"]} />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/partners_reg" element={<PartnersReg />} />
            <Route path="/affiliate" element={<Affiliate />} />
            <Route path="/affiliate-dashboard" element={<AffiliateDashboard />} />
            <Route path="/newadmin" element={<NewAdmin />} />
            <Route path="/newadmin2" element={<NewAdmin2 />} />
            <Route path="/useradmin2" element={<UserAdmin2 />} />
            <Route path="/useradmin3" element={<UserAdmin3 />} />
            <Route path="/aboutus2" element={<AboutUs2 />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/pricing2" element={<Pricing2 />} />
            <Route path="/pricing3" element={<Pricing3 />} />
            <Route path="/smb-pricing" element={<SMBPricing />} />
            <Route path="/pitchdeck" element={<PitchDeck />} />
            <Route path="/tsmt-pitch" element={<TSMTPitch />} />
            <Route path="/tsmt-pitch-therapists" element={<TSMTPitchTherapists />} />
            <Route path="/tsmt-webinar" element={<TSMTWebinar />} />
            <Route path="/tsmt-partner" element={<TSMTPartner />} />
            <Route path="/contractors" element={<Contractors />} />
            <Route path="/tsmt-kommunikacio-playbook" element={<TSMTKommunikacioPlaybook />} />
            <Route path="/for-users" element={<ForUsers />} />
            <Route path="/users" element={<Users />} />
            <Route path="/for-users/login" element={<UserLogin />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/onboarding2" element={<Onboarding2 />} />
            <Route path="/onboarding3" element={<Onboarding3 />} />
            <Route path="/onboarding4" element={<Onboarding4 />} />
            <Route path="/onboarding5" element={<Onboarding5 />} />
            <Route path="/asszisztens-naptar" element={<AsszisztensNaptar />} />
            <Route path="/selection" element={<Selection />} />
            <Route path="/ai-assistant" element={<AiAssistant />} />
          <Route path="/ai-assistant2" element={<AiAssistant2 />} />
          <Route path="/ai-assistant3" element={<AiAssistant3 />} />
            <Route path="/instagram-analyzer" element={<InstagramAnalyzer />} />

            {/* VRichard - Luxury Lifestyle Photographer */}
            <Route path="/vrichard" element={<VRichard />} />
            <Route path="/vrichard/product/:productId" element={<VRichardProductDetail />} />
            <Route path="/vrichard/service/:serviceId" element={<VRichardServiceDetail />} />
            <Route path="/vrichard/booking/:serviceId" element={<VRichardBooking />} />

            {/* New photographer page */}
            <Route path="/nyulandrea" element={<VojcehovszkijRichard />} />
            <Route path="/nyulandrea/content" element={<NyulAndreaContent />} />
            <Route
              path="/nyulandrea/content/:id"
              element={<NyulAndreaContentDetails />}
            />

            {/* Face Team routes */}
            <Route path="/faceteam" element={<FaceTeam />} />
            <Route path="/faceteam/aboutus" element={<FaceTeamAboutUs />} />
            <Route path="/faceteam/shows" element={<FaceTeamShows />} />
            <Route
              path="/faceteam/shows/:showId"
              element={<FaceTeamShowDetail />}
            />
            <Route path="/faceteam/content" element={<FaceTeamContent />} />
            <Route
              path="/faceteam/content/:contentId"
              element={<FaceTeamContentDetails />}
            />
            <Route
              path="/faceteam/category/:categoryId"
              element={<FaceTeamServiceCategory />}
            />
            <Route
              path="/faceteam/category/:categoryId/service/:serviceId"
              element={<FaceTeamServiceDetail />}
            />
            <Route
              path="/faceteam/service/:serviceId"
              element={<FaceTeamServiceDetail />}
            />
            <Route path="/faceteam/chat" element={<FaceTeamChat />} />
            <Route
              path="/faceteam/video-message"
              element={<FaceTeamVideoMessage />}
            />

            {/* Gonda-Geszler Cili route */}
            <Route path="/gonda-cili" element={<GondaCili />} />
            <Route path="/gonda-cili/kapcsolat" element={<GondaCiliKapcsolat />} />
            <Route path="/gonda-cili/workshop/istenno-rituale" element={<WorkshopDetail />} />
            <Route path="/idopontfoglalas/:serviceId" element={<ServiceDetail />} />

            <Route path="/user-admin" element={<UserAdmin />}>
              <Route index element={<UserDashboard />} />
              <Route path="dashboard" element={<UserDashboard />} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="purchases" element={<UserPurchases />} />
              <Route path="appointments" element={<UserAppointments />} />
              <Route path="workshops" element={<UserWorkshops />} />
              <Route path="videos" element={<UserVideoRequests />} />
              <Route path="questions" element={<UserQuestions />} />
              <Route
                path="workshops/:subscriptionId"
                element={<UserWorkshops />}
              />
            </Route>

            <Route path="/anyahasa" element={<AnyaHasa />} />
            <Route
              path="/anyahasa/video_message"
              element={<AnyaHasaPersonalVideo />}
            />
            <Route path="/anyahasa/chat" element={<AnyaHasaChat />} />
            <Route
              path="/anyahasa/one_to_one_meeting"
              element={<AnyaHasaOneToOneConsultation />}
            />
            <Route
              path="/anyahasa/group_meeting"
              element={<AnyaHasaGroupConsultation />}
            />
            <Route
              path="/anyahasa/group-consultation/regeneration-program"
              element={<AnyaHasaPostpartumProgram />}
            />
            <Route
              path="/anyahasa/group-consultation/pilates-program"
              element={<AnyaHasaGroupPilatesProgram />}
            />
            <Route
              path="/anyahasa/content"
              element={<AnyaHasaDigitalContent />}
            />
            <Route
              path="/anyahasa/interactive"
              element={<AnyaHasaInteractive />}
            />

            {/* Új Tanulom Magam oldalak */}
            <Route path="/tanulommagam" element={<TanulomMagamNew />} />
            <Route
              path="/tanulommagam/rolam"
              element={<TanulomMagamRolamNew />}
            />
            <Route
              path="/tanulommagam/kapcsolat-new"
              element={<TanulomMagamKapcsolatNew />}
            />
            <Route
              path="/tanulommagam/pszichodieta"
              element={<TanulomMagamPszichodieta />}
            />
            <Route
              path="/tanulommagam/idomenedzsment"
              element={<TanulomMagamIdomenedzsment />}
            />


            <Route path="/sarkoziakos" element={<SarkoziAkos />} />
            <Route path="/sarkoziakos/video-message" element={<PersonalVideo />} />
            <Route path="/sarkoziakos/chat" element={<PrivateMessage />} />
            <Route path="/sarkoziakos/content" element={<DigitalContent />} />
            <Route path="/sarkoziakos/one-to-one-meeting" element={<Consultation />} />
            <Route path="/sarkoziakos/group-meeting" element={<GroupConsultation />} />
            <Route path="/sarkoziakos/bundle" element={<Packages />} />
            <Route path="/burjanfanni" element={<BurjanFanni />} />
            <Route
              path="/burjanfanni/interactive"
              element={<BurjanFanniInteractive />}
            />
            <Route
              path="/burjanfanni/interactive/:featureId"
              element={<BurjanFanniInteractiveDetails />}
            />
            <Route
              path="/burjanfanni/subscription"
              element={<BurjanFanniSubscription />}
            />
            <Route path="/burjanfanni/video-message" element={<BurjanFanniPersonalVideoSelect />} />
            <Route path="/burjanfanni/video-message/form" element={<BurjanFanniPersonalVideoForm />} />
            <Route path="/burjanfanni/chat" element={<BurjanFanniPrivateMessage />} />
            <Route path="/burjanfanni/content" element={<BurjanFanniDigitalContent />} />
            <Route path="/burjanfanni/content/:id" element={<BurjanFanniDigitalContentDetails />} />
            <Route path="/burjanfanni/one-to-one-meeting" element={<BurjanFanniConsultation />} />
            <Route path="/burjanfanni/group-meeting" element={<BurjanFanniGroupConsultation />} />
            <Route path="/burjanfanni/bundle" element={<BurjanFanniPackages />} />
            <Route path="/burjanfanni/bundle/:id" element={<BurjanFanniPackageDetails />} />
            <Route
              path="/burjanfanni/personal-meeting"
              element={<BurjanFanniPersonalMeeting />}
            />
            <Route
              path="/burjanfanni/personal-meeting/form"
              element={<BurjanFanniPersonalMeetingForm />}
            />
            <Route path="/pumpedgabo" element={<PumpedGabo />} />
            <Route path="/pumpedgabo/gaboland" element={<Gaboland />} />
            <Route
              path="/pumpedgabo/interactive"
              element={<PumpedGaboInteractive />}
            />
            <Route
              path="/pumpedgabo/gaboland-voting"
              element={<GabolandVoting />}
            />
            <Route
              path="/pumpedgabo/gaboland-quiz"
              element={<GabolandQuiz />}
            />
            <Route
              path="/pumpedgabo/gaboland-challenge"
              element={<GabolandChallenge />}
            />
            <Route
              path="/pumpedgabo/gaboland-giveaway"
              element={<GabolandGiveaway />}
            />
            <Route path="/pumpedgabo/content" element={<PumpedGaboDigitalContent />} />
            <Route path="/pumpedgabo/content/:id" element={<DigitalContentDetails />} />
            <Route path="/pumpedgabo/chat" element={<PumpedGaboPrivateMessage />} />
            <Route path="/pumpedgabo/video-message" element={<PumpedGaboPersonalVideo />} />
            <Route path="/pumpedgabo/group-meeting" element={<PumpedGaboGroupConsultation />} />
            <Route path="/pumpedgabo/bundle" element={<PumpedGaboPackages />} />
            <Route path="/pumpedgabo/bundle/:id" element={<PumpedGaboPackageDetails />} />
            <Route
              path="/pumpedgabo/personal-meeting"
              element={<PumpedGaboPersonalMeeting />}
            />
            <Route
              path="/pumpedgabo/workshop"
              element={<PumpedGaboWorkshop />}
            />
            <Route
              path="/pumpedgabo/workshop/:id"
              element={<WorkshopDetails />}
            />

            <Route path="/szakacsgeri" element={<SzakacsGeri />} />
            <Route path="/szakacsgeri/chat" element={<SzakacsGeriChat />} />
            <Route path="/szakacsgeri/video_message" element={<SzakacsGeriVideoMessage />} />
            <Route path="/szakacsgeri/content" element={<SzakacsGeriContent />} />
            <Route path="/szakacsgeri/group_meeting" element={<SzakacsGeriGroupMeeting />} />
            <Route path="/szakacsgeri/bundle" element={<SzakacsGeriBundle />} />

            <Route
              path="/admin/*"
              element={
                <AdminLayout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/orders" element={<Orders />} />
                    <Route path="/messages" element={<Messages />} />
                    <Route path="/customers" element={<Customers />} />
                    <Route path="/content" element={<Content />} />
                    <Route path="/content/new" element={<ContentEdit />} />
                    <Route path="/content/edit/:id" element={<ContentEdit />} />
                    <Route path="/packages/new" element={<PackageEdit />} />
                    <Route
                      path="/packages/edit/:id"
                      element={<PackageEdit />}
                    />
                    {/* <Route path="/packages" element={<AdminPackages />} /> */}
                    {/* <Route path="/settings" element={<SettingsPage />} /> */}
                    <Route path="/page-setup" element={<PageSetup />} />
                    <Route path="/group-calls" element={<GroupCalls />} />
                    <Route path="/workshops" element={<Workshops />} />
                    <Route path="/subscriptions" element={<Subscriptions />} />
                    <Route path="/finances" element={<Finances />} />
                    <Route path="/reviews" element={<Reviews />} />
                    <Route path="/ai-builder" element={<AIBuilder />} />
                    <Route
                      path="/interactive-data"
                      element={<InteractiveData />}
                    />
                    <Route path="/domain-selection" element={<DomainSelection />} />
                    <Route path="/domain-setup" element={<DomainSetup />} />
                    <Route path="/domain-purchase" element={<DomainPurchase />} />
                    
                    <Route path="/subdomain-success" element={<SubdomainSuccess />} />
                    <Route path="/social" element={<SocialOverview />} />
                    <Route path="/social/calendar" element={<SocialCalendarPage />} />
                    <Route path="/social/post" element={<SocialPostEditor />} />
                    <Route path="/social/post/:id" element={<SocialPostEditor />} />
                    <Route path="/social/story" element={<SocialStoryEditor />} />
                    <Route path="/social/story/:id" element={<SocialStoryEditor />} />
                  </Routes>
                </AdminLayout>
              }
            />
            <Route path="/admin/publish-registration" element={<PublishRegistration />} />
            <Route path="/admin2/*" element={<Admin2 />} />
            <Route path="/for-creators" element={<IndexOriginal />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/login2" element={<Login2 />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/rounds" element={<Rounds />} />
            <Route path="/creator-world" element={<CreatorWorld />} />
            <Route path="/creator-world2" element={<CreatorWorld2 />} />
            <Route path="/creator-world3" element={<CreatorWorld3 />} />
          </Routes>
          </Suspense>
        </Router>
        </TranslationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
